import { Component, AfterViewInit, ChangeDetectorRef, TemplateRef, ViewChild, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { TableComponent, TableColumn, PaginationConfig, withCellTemplates } from '../../../shared/components/primeng/table/table.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { ToasterService } from '../../../services/toaster.service';
import { FormatService } from '../../../shared/services/format.service';
import { DiscussionApiService } from '@angular-workspace/services';

type DiscussionTab = 'pending' | 'history' | 'comments';

/** Pills inside Review Comments tab */
type ReviewCommentsPill = 'pending' | 'history' | 'reported-comments' | 'reported-history';

export interface PendingReview {
  id: string;
  userName: string;
  discussionName: string;
  forumName: string;
  category: string;
  submissionDate: string;
}

export interface ReviewHistory {
  id: string;
  userName: string;
  discussionName: string;
  forumName: string;
  category: string;
  submissionDate: string;
  reviewDate: string;
  status: 'Approved' | 'Rejected' | 'Action Required';
}

/** Row for Pending Reviews (comments) pill */
export interface PendingReviewCommentRow {
  id: string;
  userName: string;
  discussionName: string;
  comment: string;
  submissionDate: string;
}

/** Row for Review History (comments) pill */
export interface ReviewHistoryCommentRow {
  id: string;
  userName: string;
  discussionName: string;
  comment: string;
  submissionDate: string;
  reviewDate: string;
  status: 'Approved' | 'Rejected' | 'Action Required';
}

/** Row for Reported Comments pill */
export interface ReportedCommentRow {
  id: string;
  reportedUserName: string;
  discussionName: string;
  reason: string;
  comment: string;
  reportedDate: string;
}

/** Row for Report History pill */
export interface ReportHistoryRow {
  id: string;
  reportedUserName: string;
  discussionName: string;
  reason: string;
  comment: string;
  reportedDate: string;
  reviewDate: string;
  status: 'Approved' | 'Rejected' | 'Action Required';
}

@Component({
  selector: 'app-discussion-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    ModalComponent
  ],
  templateUrl: './discussion-home.component.html',
  styleUrl: './discussion-home.component.scss'
})
export class DiscussionHomeComponent implements OnInit, AfterViewInit {
  activeTab: DiscussionTab = 'pending';
  /** Active pill inside Review Comments tab */
  activeCommentsPill: ReviewCommentsPill = 'pending';
  openPendingDropdownId: string | null = null;
  /** Row index for open dropdown in Review Comments Pending (for z-index) */
  commentsPendingDropdownRowIndex: number | null = null;
  showReviewModal: boolean = false;
  selectedReview: PendingReview | null = null;
  /** Discussion body HTML from getReview() response data.content */
  selectedReviewContent: string = '';
  /** Reject checkbox selected */
  showRejectInput: boolean = false;
  /** Feedback checkbox selected */
  showFeedbackInput: boolean = false;
  /** Single comment for either Reject or Feedback action */
  decisionComment: string = '';
  isLoadingHistory: boolean = false;
  isLoadingPending: boolean = false;

  @ViewChild('pendingActionCell', { static: false }) pendingActionCellTemplate!: TemplateRef<any>;
  @ViewChild('pendingCategoryCell', { static: false }) pendingCategoryCellTemplate!: TemplateRef<any>;
  @ViewChild('historyStatusCell', { static: false }) historyStatusCellTemplate!: TemplateRef<any>;

  pendingColumns: TableColumn[] = [
    {
      key: 'userName',
      label: 'UserName',
      sortable: false,
      width: '15%'
    },
    {
      key: 'discussionName',
      label: 'Discussion Name',
      sortable: false,
      width: '20%'
    },
    {
      key: 'forumName',
      label: 'ForumName',
      sortable: false,
      width: '15%'
    },
    {
      key: 'category',
      label: 'Category',
      sortable: false,
      width: '15%'
    },
    {
      key: 'submissionDate',
      label: 'Submission Date',
      sortable: false,
      width: '15%'
    },
    {
      key: 'action',
      label: 'Actions',
      sortable: false,
      width: '20%',
      align: 'left',
      fixed: 'right'
    }
  ];

  pendingData: PendingReview[] = [];

  pendingPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 3,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  // Review History table configuration
  historyColumns: TableColumn[] = [
    {
      key: 'userName',
      label: 'Username',
      sortable: false,
      width: '12%'
    },
    {
      key: 'discussionName',
      label: 'Discussion Name',
      sortable: false,
      width: '18%'
    },
    {
      key: 'forumName',
      label: 'Forum name',
      sortable: false,
      width: '15%'
    },
    {
      key: 'category',
      label: 'Category',
      sortable: false,
      width: '12%'
    },
    {
      key: 'submissionDate',
      label: 'Submission Date',
      sortable: false,
      width: '15%'
    },
    {
      key: 'reviewDate',
      label: 'Review Date',
      sortable: false,
      width: '15%'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: '13%'
    }
  ];

  historyData: ReviewHistory[] = [
    // Sample data - to be replaced with actual API data
    {
      id: '1',
      userName: 'John Doe',
      discussionName: 'AI Model Performance Discussion',
      forumName: 'Machine Learning Forum',
      category: 'Technical',
      submissionDate: '2024-12-15 10:00:00',
      reviewDate: '2024-12-18 14:30:00',
      status: 'Approved'
    },
    {
      id: '2',
      userName: 'Jane Smith',
      discussionName: 'Data Privacy Best Practices',
      forumName: 'Data Governance Forum',
      category: 'Compliance',
      submissionDate: '2024-12-10 09:15:00',
      reviewDate: '2024-12-12 16:45:00',
      status: 'Rejected'
    },
    {
      id: '3',
      userName: 'Bob Johnson',
      discussionName: 'Model Deployment Strategies',
      forumName: 'DevOps Forum',
      category: 'Technical',
      submissionDate: '2024-12-20 11:20:00',
      reviewDate: '2024-12-22 10:00:00',
      status: 'Action Required'
    },
    {
      id: '4',
      userName: 'Alice Williams',
      discussionName: 'Ethical AI Guidelines',
      forumName: 'AI Ethics Forum',
      category: 'Compliance',
      submissionDate: '2024-12-08 13:00:00',
      reviewDate: '2024-12-10 09:30:00',
      status: 'Approved'
    },
    {
      id: '5',
      userName: 'Charlie Brown',
      discussionName: 'Data Quality Standards',
      forumName: 'Data Management Forum',
      category: 'Technical',
      submissionDate: '2024-12-12 15:45:00',
      reviewDate: '2024-12-15 11:15:00',
      status: 'Action Required'
    }
  ];

  historyPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 5,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  // --- Review Comments (pills) tables ---
  openCommentsPendingDropdownId: string | null = null;
  openCommentsReportedDropdownId: string | null = null;
  /** Position for fixed overlay dropdowns (Comments tab) */
  commentsPendingDropdownPosition: { top: number; left: number; width: number } | null = null;
  commentsReportedDropdownPosition: { top: number; left: number; width: number } | null = null;
  commentsReportedDropdownRowIndex: number | null = null;
  showCommentReviewModal = false;
  selectedCommentReview: PendingReviewCommentRow | null = null;
  /** Rejection reason (required when Reject is selected) */
  rejectionReason = '';
  /** Reject checkbox selected in comment review modal */
  showCommentRejectInput = false;
  isLoadingCommentsPending = false;
  isLoadingCommentsHistory = false;
  isLoadingCommentsReported = false;
  isLoadingCommentsReportHistory = false;

  commentsPendingColumns: TableColumn[] = [
    { key: 'userName', label: 'User Name', sortable: false, width: '15%' },
    { key: 'discussionName', label: 'Discussion Name', sortable: false, width: '20%' },
    { key: 'comment', label: 'Comment', sortable: false, width: '30%' },
    { key: 'submissionDate', label: 'Submission Date', sortable: false, width: '15%' },
    { key: 'action', label: 'Action', sortable: false, width: '20%', align: 'left', fixed: 'right' }
  ];
  commentsPendingData: PendingReviewCommentRow[] = [];
  commentsPendingPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  commentsHistoryColumns: TableColumn[] = [
    { key: 'userName', label: 'User Name', sortable: false, width: '12%' },
    { key: 'discussionName', label: 'Discussion Name', sortable: false, width: '18%' },
    { key: 'comment', label: 'Comment', sortable: false, width: '25%' },
    { key: 'submissionDate', label: 'Submission Date', sortable: false, width: '12%' },
    { key: 'reviewDate', label: 'Review Date', sortable: false, width: '12%' },
    { key: 'status', label: 'Status', sortable: false, width: '12%' }
  ];
  commentsHistoryData: ReviewHistoryCommentRow[] = [];
  commentsHistoryPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  commentsReportedColumns: TableColumn[] = [
    { key: 'reportedUserName', label: 'Reported User Name', sortable: false, width: '15%' },
    { key: 'discussionName', label: 'Discussion Name', sortable: false, width: '18%' },
    { key: 'reason', label: 'Reason', sortable: false, width: '15%' },
    { key: 'comment', label: 'Comment', sortable: false, width: '22%' },
    { key: 'reportedDate', label: 'Reported Date', sortable: false, width: '12%' },
    { key: 'action', label: 'Action', sortable: false, width: '18%', align: 'left', fixed: 'right' }
  ];
  commentsReportedData: ReportedCommentRow[] = [];
  commentsReportedPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  commentsReportHistoryColumns: TableColumn[] = [
    { key: 'reportedUserName', label: 'Reported User Name', sortable: false, width: '12%' },
    { key: 'discussionName', label: 'Discussion Name', sortable: false, width: '15%' },
    { key: 'reason', label: 'Reason', sortable: false, width: '12%' },
    { key: 'comment', label: 'Comment', sortable: false, width: '18%' },
    { key: 'reportedDate', label: 'Reported Date', sortable: false, width: '10%' },
    { key: 'reviewDate', label: 'Review Date', sortable: false, width: '10%' },
    { key: 'status', label: 'Status', sortable: false, width: '12%' }
  ];
  commentsReportHistoryData: ReportHistoryRow[] = [];
  commentsReportHistoryPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: false
  };

  @ViewChild('commentsPendingActionCell', { static: false }) commentsPendingActionCellTemplate!: TemplateRef<any>;
  @ViewChild('commentsHistoryStatusCell', { static: false }) commentsHistoryStatusCellTemplate!: TemplateRef<any>;
  @ViewChild('commentsReportedActionCell', { static: false }) commentsReportedActionCellTemplate!: TemplateRef<any>;
  @ViewChild('commentsReportHistoryStatusCell', { static: false }) commentsReportHistoryStatusCellTemplate!: TemplateRef<any>;

  constructor(
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService,
    private formatService: FormatService,
    private discussionApiService: DiscussionApiService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Restore tab from URL query param (e.g. ?tab=history) for refresh persistence
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'pending' || tab === 'history' || tab === 'comments') {
      this.activeTab = tab;
    }
    // Load data for default/active tab
    this.loadPendingDiscussions();
    this.loadReviewHistory();
    if (this.activeTab === 'comments') {
      this.setCommentsPill(this.activeCommentsPill);
    }
  }

  ngAfterViewInit(): void {
    // Assign cell templates using immutable update so table re-renders (fixes Action dropdown on first load)
    setTimeout(() => {
      const pendingUpdates: { key: string; cellTemplate: TemplateRef<any> }[] = [];
      if (this.pendingActionCellTemplate) pendingUpdates.push({ key: 'action', cellTemplate: this.pendingActionCellTemplate });
      if (this.pendingCategoryCellTemplate) pendingUpdates.push({ key: 'category', cellTemplate: this.pendingCategoryCellTemplate });
      if (pendingUpdates.length) {
        this.pendingColumns = withCellTemplates(this.pendingColumns, pendingUpdates);
      }
      if (this.historyStatusCellTemplate) {
        this.historyColumns = withCellTemplates(this.historyColumns, [{ key: 'status', cellTemplate: this.historyStatusCellTemplate }]);
      }
      // Review Comments templates are at component root, so ViewChild finds them
      this.reassignCommentsPendingCellTemplates();
      this.reassignCommentsHistoryCellTemplates();
      this.reassignCommentsReportedCellTemplates();
      this.reassignCommentsReportHistoryCellTemplates();
      this.cdr.detectChanges();
    }, 100);
  }

  setCommentsPill(pill: ReviewCommentsPill): void {
    this.activeCommentsPill = pill;
    this.closeCommentsPendingDropdown();
    this.closeCommentsReportedDropdown();
    if (pill === 'pending') this.loadCommentsPendingReview();
    else if (pill === 'history') this.loadCommentsReviewHistory();
    else if (pill === 'reported-comments') this.loadCommentsReported();
    else if (pill === 'reported-history') this.loadCommentsReportedHistory();
    this.cdr.detectChanges();
    // Assign cell templates when pane is visible (immutable update ensures table re-renders)
    setTimeout(() => {
      const updates: { key: string; cellTemplate: TemplateRef<any> }[] = [];
      if (pill === 'pending' && this.commentsPendingActionCellTemplate) updates.push({ key: 'action', cellTemplate: this.commentsPendingActionCellTemplate });
      if (pill === 'history' && this.commentsHistoryStatusCellTemplate) updates.push({ key: 'status', cellTemplate: this.commentsHistoryStatusCellTemplate });
      if (pill === 'reported-comments' && this.commentsReportedActionCellTemplate) updates.push({ key: 'action', cellTemplate: this.commentsReportedActionCellTemplate });
      if (pill === 'reported-history' && this.commentsReportHistoryStatusCellTemplate) updates.push({ key: 'status', cellTemplate: this.commentsReportHistoryStatusCellTemplate });
      if (updates.length) {
        if (pill === 'pending' || pill === 'history') {
          const cols = pill === 'pending' ? this.commentsPendingColumns : this.commentsHistoryColumns;
          const updated = withCellTemplates(cols, updates);
          if (pill === 'pending') this.commentsPendingColumns = updated; else this.commentsHistoryColumns = updated;
        } else {
          const cols = pill === 'reported-comments' ? this.commentsReportedColumns : this.commentsReportHistoryColumns;
          const updated = withCellTemplates(cols, updates);
          if (pill === 'reported-comments') this.commentsReportedColumns = updated; else this.commentsReportHistoryColumns = updated;
        }
      }
      this.cdr.detectChanges();
    }, 0);
  }

  toggleCommentsPendingDropdown(event: Event, row: PendingReviewCommentRow): void {
    event.stopPropagation();
    if (this.openCommentsPendingDropdownId === row.id) {
      this.closeCommentsPendingDropdown();
      this.cdr.detectChanges();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.commentsPendingDropdownPosition = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    this.openCommentsPendingDropdownId = row.id;
    this.commentsPendingDropdownRowIndex = this.commentsPendingData.findIndex((r) => r.id === row.id);
    this.cdr.detectChanges();
  }
  get openCommentsPendingRow(): PendingReviewCommentRow | null {
    if (!this.openCommentsPendingDropdownId) return null;
    return this.commentsPendingData.find((r) => r.id === this.openCommentsPendingDropdownId) ?? null;
  }
  isCommentsPendingDropdownOpen(row: PendingReviewCommentRow): boolean {
    return this.openCommentsPendingDropdownId === row.id;
  }
  closeCommentsPendingDropdown(): void {
    this.openCommentsPendingDropdownId = null;
    this.commentsPendingDropdownRowIndex = null;
    this.commentsPendingDropdownPosition = null;
  }
  handleCommentsPendingAction(action: string, row: PendingReviewCommentRow): void {
    this.closeCommentsPendingDropdown();
    if (action === 'viewReview') {
      this.selectedCommentReview = row;
      this.showCommentReviewModal = true;
      setTimeout(() => this.cdr.detectChanges(), 0);
    }
  }

  closeCommentReviewModal(): void {
    this.showCommentReviewModal = false;
    this.selectedCommentReview = null;
    this.rejectionReason = '';
    this.showCommentRejectInput = false;
  }

  onCommentRejectDecisionChange(checked: boolean): void {
    if (checked) {
      this.rejectionReason = '';
      this.scrollToCommentDecisionTextarea();
    }
  }

  private scrollToCommentDecisionTextarea(): void {
    setTimeout(() => {
      const el = document.getElementById('comment-decision-input');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  async approveCommentReview(): Promise<void> {
    if (!this.selectedCommentReview) return;
    try {
      await this.discussionApiService.reviewComment(this.selectedCommentReview.id, {
        status: 'APPROVED',
        comment: this.selectedCommentReview.comment || '',
        attachments: { add: [], remove: [] },
      });
      this.toasterService.success('Comment approved', 3000);
      this.closeCommentReviewModal();
      this.closeCommentsPendingDropdown();
      setTimeout(() => this.loadCommentsPendingReview(), 350);
    } catch (error: any) {
      this.toasterService.error(error?.error?.message || error?.message || 'Failed to approve comment', 3000);
    }
  }

  async rejectCommentReview(): Promise<void> {
    if (!this.selectedCommentReview) return;
    if (!this.rejectionReason?.trim()) {
      this.toasterService.error('Please provide a rejection reason', 3000);
      return;
    }
    try {
      await this.discussionApiService.reviewComment(this.selectedCommentReview.id, {
        status: 'REJECTED',
        comment: this.selectedCommentReview.comment || '',
        attachments: { add: [], remove: [] },
        rejection_reason: this.rejectionReason.trim(),
      });
      this.toasterService.success('Comment rejected', 3000);
      this.closeCommentReviewModal();
      this.closeCommentsPendingDropdown();
      setTimeout(() => this.loadCommentsPendingReview(), 350);
    } catch (error: any) {
      this.toasterService.error(error?.error?.message || error?.message || 'Failed to reject comment', 3000);
    }
  }

  toggleCommentsReportedDropdown(event: Event, row: ReportedCommentRow): void {
    event.stopPropagation();
    if (this.openCommentsReportedDropdownId === row.id) {
      this.closeCommentsReportedDropdown();
      this.cdr.detectChanges();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.commentsReportedDropdownPosition = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    this.openCommentsReportedDropdownId = row.id;
    this.commentsReportedDropdownRowIndex = this.commentsReportedData.findIndex((r) => r.id === row.id);
    this.cdr.detectChanges();
  }
  get openCommentsReportedRow(): ReportedCommentRow | null {
    if (!this.openCommentsReportedDropdownId) return null;
    return this.commentsReportedData.find((r) => r.id === this.openCommentsReportedDropdownId) ?? null;
  }
  isCommentsReportedDropdownOpen(row: ReportedCommentRow): boolean {
    return this.openCommentsReportedDropdownId === row.id;
  }
  closeCommentsReportedDropdown(): void {
    this.openCommentsReportedDropdownId = null;
    this.commentsReportedDropdownRowIndex = null;
    this.commentsReportedDropdownPosition = null;
  }
  handleCommentsReportedAction(action: string, row: ReportedCommentRow): void {
    this.closeCommentsReportedDropdown();
    if (action === 'viewReview') {
      this.toasterService.info('View Review for reported comment: ' + (row.discussionName || row.id), 3000);
      // TODO: open reported comment review modal
    }
  }

  setTab(tab: DiscussionTab): void {
    this.activeTab = tab;
    // Persist tab in URL so it survives refresh
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    if (tab === 'comments') {
      this.setCommentsPill('pending');
    }
    
    // Load data when switching tabs
    if (tab === 'pending') {
      this.loadPendingDiscussions();
    } else if (tab === 'history') {
      this.loadReviewHistory();
    } else if (tab === 'comments') {
      // setCommentsPill('pending') above triggers loadCommentsPendingReview()
    }
    
    // Reassign templates when tab changes (immutable update ensures table re-renders)
    setTimeout(() => {
      if (tab === 'pending') {
        const updates: { key: string; cellTemplate: TemplateRef<any> }[] = [];
        if (this.pendingActionCellTemplate) updates.push({ key: 'action', cellTemplate: this.pendingActionCellTemplate });
        if (this.pendingCategoryCellTemplate) updates.push({ key: 'category', cellTemplate: this.pendingCategoryCellTemplate });
        if (updates.length) this.pendingColumns = withCellTemplates(this.pendingColumns, updates);
      } else if (tab === 'history' && this.historyStatusCellTemplate) {
        this.historyColumns = withCellTemplates(this.historyColumns, [{ key: 'status', cellTemplate: this.historyStatusCellTemplate }]);
      }
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * Format forum name: replace underscores with space and Pascal/title case (e.g. GETTING_STARTED -> Getting Started)
   */
  formatForumName(value: string): string {
    if (!value) return '';
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /**
   * Get category display label (Dataset, Model, etc.) from API category value
   */
  getCategoryDisplayLabel(category: string): string {
    const iconType = this.getCategoryIconType(category || '');
    if (iconType === 'dataset') return 'Dataset';
    if (iconType === 'model') return 'Model';
    return this.formatService.formatAssetType(category || '') || category || '—';
  }

  /**
   * Get category icon type for Dataset/Model (null for others)
   */
  getCategoryIconType(category: string): 'dataset' | 'model' | null {
    if (!category) return null;
    const normalized = String(category).replace(/[\s_-]/g, '').toUpperCase();
    if (normalized === 'DATABANKS' || normalized.includes('DATABANK')) return 'dataset';
    if (normalized === 'AIMODEL' || normalized === 'AIMODELS' || normalized === 'MODEL') return 'model';
    return null;
  }

  getStatusClass(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'approved') {
      return 'status-chip--approved';
    } else if (statusLower === 'rejected') {
      return 'status-chip--rejected';
    } else if (statusLower === 'action required') {
      return 'status-chip--action-required';
    }
    return '';
  }

  togglePendingActionDropdown(event: Event, row: PendingReview): void {
    event.stopPropagation();
    const rowId = row.id;
    const wasOpen = this.openPendingDropdownId === rowId;
    this.openPendingDropdownId = wasOpen ? null : rowId;
    
    // Position dropdown using fixed positioning to escape scroll container
    if (!wasOpen && this.openPendingDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        const dropdown = document.querySelector(`[data-pending-dropdown-id="${rowId}"]`) as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = 'max-content';
          dropdown.style.minWidth = 'max-content';
        }
      }, 0);
    }
  }

  isPendingDropdownOpen(row: PendingReview): boolean {
    return this.openPendingDropdownId === row.id;
  }

  closePendingActionDropdown(): void {
    this.openPendingDropdownId = null;
  }

  handlePendingAction(action: string, row: PendingReview): void {
    if (action === 'viewReview') {
      this.closePendingActionDropdown();
      this.loadDiscussionAndOpenReviewModal(row);
    } else if (action === 'viewDiscussion') {
      this.closePendingActionDropdown();
      // Route exists in `app.routes.ts`: `discussions/:id`
      const id = row?.id;
      if (!id) {
        this.toasterService.error(`Unable to view discussion: missing id`, 3000);
        return;
      }
      this.router.navigate(['/discussions', id]).catch((err) => {
        console.error('Failed to navigate to discussion detail:', err);
        this.toasterService.error(`Failed to open discussion: ${row.discussionName}`, 3000);
      });
    }
  }

  /**
   * Fetch discussion by ID via getReview(), then open the Review Discussion modal.
   */
  async loadDiscussionAndOpenReviewModal(row: PendingReview): Promise<void> {
    try {
      const response = await this.discussionApiService.getReview(row.id);
      this.selectedReview = row;
      this.selectedReviewContent = response?.data?.content ?? response?.content ?? '';
      this.decisionComment = '';
      this.showRejectInput = false;
      this.showFeedbackInput = false;
      this.showReviewModal = true;
    } catch (error: any) {
      console.error('Error loading discussion for review:', error);
      this.toasterService.error(
        error?.error?.message || error?.message || 'Failed to load discussion',
        3000
      );
    }
  }

  openReviewModal(row: PendingReview): void {
    this.selectedReview = row;
    this.decisionComment = '';
    this.showReviewModal = true;
  }

  closeReviewModal(): void {
    this.showReviewModal = false;
    this.selectedReview = null;
    this.selectedReviewContent = '';
    this.decisionComment = '';
    this.showRejectInput = false;
    this.showFeedbackInput = false;
  }

  /** Only one decision (Reject or Feedback) open at a time: clear Feedback when Reject is checked. */
  onRejectDecisionChange(checked: boolean): void {
    if (checked) {
      this.showFeedbackInput = false;
      this.decisionComment = '';
      this.scrollToDecisionTextarea();
    }
  }

  /** Only one decision (Reject or Feedback) open at a time: clear Reject when Feedback is checked. */
  onFeedbackDecisionChange(checked: boolean): void {
    if (checked) {
      this.showRejectInput = false;
      this.decisionComment = '';
      this.scrollToDecisionTextarea();
    }
  }

  /** Scroll the feedback text area into view when Reject or Feedback is selected. */
  private scrollToDecisionTextarea(): void {
    setTimeout(() => {
      const el = document.getElementById('decision-comment-input');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  async handleReviewAction(action: 'approve' | 'reject' | 'requestChanges'): Promise<void> {
    if (!this.selectedReview) return;

    const payload = (reviewStatus: string, comment: string) => ({
      review_status: reviewStatus,
      comment: comment?.trim() || '',
    });

    if (action === 'approve') {
      try {
        await this.discussionApiService.approveDiscussion(this.selectedReview.id, payload('APPROVED', 'Approved by admin'));
        this.toasterService.success(`Review approved for: ${this.selectedReview.discussionName}`, 3000);
        this.closeReviewModal();
        this.closePendingActionDropdown();
        // Delay refresh so modal overlay is fully removed before updating table (avoids blocking Action button clicks)
        setTimeout(() => this.loadPendingDiscussions(), 350);
      } catch (error: any) {
        console.error('Error approving discussion:', error);
        this.toasterService.error(error?.error?.message || error?.message || 'Failed to approve discussion', 3000);
      }
      return;
    }

    if (action === 'reject') {
      try {
        await this.discussionApiService.approveDiscussion(this.selectedReview.id, payload('REJECTED', this.decisionComment));
        this.toasterService.success(`Review rejected for: ${this.selectedReview.discussionName}`, 3000);
        this.closeReviewModal();
        this.closePendingActionDropdown();
        setTimeout(() => this.loadPendingDiscussions(), 350);
      } catch (error: any) {
        console.error('Error rejecting discussion:', error);
        this.toasterService.error(error?.error?.message || error?.message || 'Failed to reject discussion', 3000);
      }
      return;
    }

    if (action === 'requestChanges') {
      try {
        await this.discussionApiService.approveDiscussion(this.selectedReview.id, payload('CHANGES_REQUIRED', this.decisionComment));
        this.toasterService.success(`Changes requested for: ${this.selectedReview.discussionName}`, 3000);
        this.closeReviewModal();
        this.closePendingActionDropdown();
        setTimeout(() => this.loadPendingDiscussions(), 350);
      } catch (error: any) {
        console.error('Error requesting changes:', error);
        this.toasterService.error(error?.error?.message || error?.message || 'Failed to request changes', 3000);
      }
    }
  }

  /** Reassign action/category cell templates after data refresh (fixes Action dropdown on first load). */
  private reassignPendingCellTemplates(): void {
    const updates: { key: string; cellTemplate: TemplateRef<any> }[] = [];
    if (this.pendingActionCellTemplate) updates.push({ key: 'action', cellTemplate: this.pendingActionCellTemplate });
    if (this.pendingCategoryCellTemplate) updates.push({ key: 'category', cellTemplate: this.pendingCategoryCellTemplate });
    if (updates.length) {
      this.pendingColumns = withCellTemplates(this.pendingColumns, updates);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close pending dropdown when clicking outside (allow overlay clicks)
    if (this.openPendingDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closePendingActionDropdown();
    }
    // Close Review Comments pills dropdowns when clicking outside (allow overlay clicks)
    if (this.openCommentsPendingDropdownId !== null && !target.closest('.action-cell-wrapper') && !target.closest('.action-dropdown-overlay')) {
      this.closeCommentsPendingDropdown();
    }
    if (this.openCommentsReportedDropdownId !== null && !target.closest('.action-cell-wrapper') && !target.closest('.action-dropdown-overlay')) {
      this.closeCommentsReportedDropdown();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    // Close pending dropdown on scroll
    if (this.openPendingDropdownId !== null) {
      this.closePendingActionDropdown();
    }
    if (this.openCommentsPendingDropdownId !== null) {
      this.closeCommentsPendingDropdown();
    }
    if (this.openCommentsReportedDropdownId !== null) {
      this.closeCommentsReportedDropdown();
    }
  }

  /**
   * Extract list of items from API response (handles multiple response shapes).
   */
  private getPendingListFromResponse(response: any): any[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const data = response.data;
    if (data !== undefined) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.discussions)) return data.discussions;
      if (Array.isArray(data?.list)) return data.list;
    }
    if (Array.isArray(response.discussions)) return response.discussions;
    if (Array.isArray(response.list)) return response.list;
    return [];
  }

  /**
   * Load pending discussions from API
   */
  async loadPendingDiscussions(): Promise<void> {
    try {
      this.isLoadingPending = true;
      this.cdr.detectChanges();

      const filters = {
        type: ['PUBLIC', 'GENERAL', 'GETTING_STARTED', 'PRODUCT_FEEDBACK', 'PRODUCT_ANNOUNCEMENTS'],
        status: ['PENDING'],
        tags: null,
        time_range: null
      };

      const response = await this.discussionApiService.adminPendingDiscussion({
        filters,
        sort_by: 'updated_at',
        sort_order: 'desc',
        page: 1,
        limit: 10
      });

      const rawList = this.getPendingListFromResponse(response);
      this.pendingData = rawList.map((item: any) => ({
        id: item.id || item.discussion_id || '',
        userName: item.user?.name ?? item.user_name ?? item.userName ?? item.author_name ?? item.created_by ?? 'Unknown',
        discussionName: item.discussion_name ?? item.title ?? item.name ?? 'Untitled',
        forumName: this.formatForumName(item.type ?? item.forum_name ?? item.forumName ?? '') || 'General',
        category: item.category ?? item.type ?? 'General',
        submissionDate: this.formatService.formatDate(item.submission_date ?? item.submitted_at ?? item.created_at ?? item.updated_at ?? '') || ''
      }));

      const pagination = response?.pagination ?? response?.data?.pagination;
      if (pagination) {
        this.pendingPagination = {
          ...this.pendingPagination,
          totalItems: pagination.total ?? pagination.total_items ?? this.pendingData.length,
          currentPage: pagination.page ?? pagination.current_page ?? 1,
          pageSize: pagination.limit ?? pagination.page_size ?? 10
        };
      } else {
        this.pendingPagination = {
          ...this.pendingPagination,
          totalItems: this.pendingData.length
        };
      }

      this.reassignPendingCellTemplates();
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error loading pending discussions:', error);
      
      // Check if it's a 500 Internal Server Error
      const statusCode = error?.status || error?.error?.status || error?.error?.statusCode;
      if (statusCode === 500) {
        // For 500 errors, show empty state instead of error message
        this.pendingData = [];
        this.pendingPagination.totalItems = 0;
        this.cdr.detectChanges();
      } else {
        // For other errors, show error toaster
        this.toasterService.error(
          error?.error?.message || error?.message || 'Failed to load pending discussions',
          3000
        );
      }
    } finally {
      this.isLoadingPending = false;
    }
  }

  /**
   * Load review history from API
   */
  async loadReviewHistory(): Promise<void> {
    try {
      this.isLoadingHistory = true;
      
      const filters = {
        type: ['PUBLIC', 'GENERAL', 'GETTING_STARTED', 'PRODUCT_FEEDBACK', 'PRODUCT_ANNOUNCEMENTS'],
        status: ['APPROVED', 'CHANGES_REQUIRED', 'REJECTED'],
        tags: null,
        time_range: null
      };

      const response = await this.discussionApiService.getReviewHistory({
        filters,
        sort_by: 'reviewed_at',
        sort_order: 'desc',
        page: 1,
        limit: 10
      });

      // Map API response to ReviewHistory interface
      if (response && response.data && Array.isArray(response.data)) {
        this.historyData = response.data.map((item: any) => ({
          id: item.id || item.discussion_id || '',
          userName: item.user?.name ?? item.user_name ?? item.userName ?? item.author_name ?? item.created_by ?? 'Unknown',
          discussionName: item.discussion_name || item.title || item.name || 'Untitled',
          forumName: this.formatForumName(item.forum_name ?? item.forumName ?? item.type ?? '') || 'General',
          category: item.category || item.type || 'General',
          submissionDate: item.submission_date || item.submitted_at || item.created_at || '',
          reviewDate: item.review_date || item.reviewed_at || item.updated_at || '',
          status: this.mapStatusToDisplay(item.status || item.review_status || '')
        }));

        // Update pagination if response includes pagination info
        if (response.pagination) {
          this.historyPagination = {
            ...this.historyPagination,
            totalItems: response.pagination.total || response.pagination.total_items || this.historyData.length,
            currentPage: response.pagination.page || response.pagination.current_page || 1,
            pageSize: response.pagination.limit || response.pagination.page_size || 10
          };
        } else {
          this.historyPagination.totalItems = this.historyData.length;
        }
      } else if (response && Array.isArray(response)) {
        // Handle case where response is directly an array
        this.historyData = response.map((item: any) => ({
          id: item.id || item.discussion_id || '',
          userName: item.user?.name ?? item.user_name ?? item.userName ?? item.author_name ?? item.created_by ?? 'Unknown',
          discussionName: item.discussion_name || item.title || item.name || 'Untitled',
          forumName: this.formatForumName(item.forum_name ?? item.forumName ?? item.type ?? '') || 'General',
          category: item.category || item.type || 'General',
          submissionDate: item.submission_date || item.submitted_at || item.created_at || '',
          reviewDate: item.review_date || item.reviewed_at || item.updated_at || '',
          status: this.mapStatusToDisplay(item.status || item.review_status || '')
        }));
        this.historyPagination.totalItems = this.historyData.length;
      }

      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error loading review history:', error);
      
      // Check if it's a 500 Internal Server Error
      const statusCode = error?.status || error?.error?.status || error?.error?.statusCode;
      if (statusCode === 500) {
        // For 500 errors, show empty state instead of error message
        this.historyData = [];
        this.historyPagination.totalItems = 0;
        this.cdr.detectChanges();
      } else {
        // For other errors, show error toaster
        this.toasterService.error(
          error?.error?.message || error?.message || 'Failed to load review history',
          3000
        );
      }
    } finally {
      this.isLoadingHistory = false;
    }
  }

  /**
   * Map API status values to display format
   */
  private mapStatusToDisplay(status: string): 'Approved' | 'Rejected' | 'Action Required' {
    const statusUpper = (status || '').toUpperCase();
    
    if (statusUpper === 'APPROVED') {
      return 'Approved';
    } else if (statusUpper === 'REJECTED') {
      return 'Rejected';
    } else if (statusUpper === 'CHANGES_REQUIRED' || statusUpper === 'CHANGES REQUIRED') {
      return 'Action Required';
    }
    
    // Default fallback
    return 'Action Required';
  }

  /**
   * Extract list from comment/report API response (handles multiple shapes).
   */
  private getCommentListFromResponse(response: any): any[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.comments)) return data.comments;
    if (Array.isArray(data?.reports)) return data.reports;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.list)) return data.list;
    return [];
  }

  /** Strip HTML tags and decode entities so comment displays as plain text in the UI. */
  private stripHtml(html: string): string {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }

  /** Load Pending Reviews (comments) pill - calls pendingReview() on page load when pill is selected. */
  async loadCommentsPendingReview(): Promise<void> {
    try {
      this.closeCommentsPendingDropdown();
      this.isLoadingCommentsPending = true;
      this.cdr.detectChanges();
      const response = await this.discussionApiService.pendingReview({
        page: this.commentsPendingPagination.currentPage,
        limit: this.commentsPendingPagination.pageSize,
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      const list = this.getCommentListFromResponse(response);
      // Reassign templates BEFORE setting data so table renders with correct cells
      this.reassignCommentsPendingCellTemplates();
      this.commentsPendingData = list.map((item: any) => ({
        id: item.id ?? item.comment_id ?? '',
        userName: item.user_name ?? item.userName ?? item.author_name ?? item.user?.name ?? 'Unknown',
        discussionName: item.discussion?.title ?? item.discussion_name ?? item.discussion_title ?? item.title ?? '—',
        comment: this.stripHtml(item.comment ?? item.content ?? item.body ?? ''),
        submissionDate: this.formatService.formatDate(item.submission_date ?? item.created_at ?? item.submitted_at ?? '') ?? ''
      }));
      const pag = response?.pagination ?? response?.data?.pagination;
      this.commentsPendingPagination = {
        ...this.commentsPendingPagination,
        totalItems: pag?.total ?? pag?.total_items ?? this.commentsPendingData.length
      };
    } catch (error: any) {
      console.error('Error loading comments pending review:', error);
      this.commentsPendingData = [];
      this.commentsPendingPagination.totalItems = 0;
    } finally {
      this.isLoadingCommentsPending = false;
      this.reassignCommentsPendingCellTemplates();
      this.cdr.detectChanges();
      // Extra reassign after DOM settles (fixes Action button after approve/reject)
      setTimeout(() => {
        this.reassignCommentsPendingCellTemplates();
        this.cdr.detectChanges();
      }, 100);
    }
  }

  /** Load Review History (comments) pill - calls reviewHistory(). */
  async loadCommentsReviewHistory(): Promise<void> {
    try {
      this.isLoadingCommentsHistory = true;
      this.cdr.detectChanges();
      const response = await this.discussionApiService.reviewHistory({
        page: this.commentsHistoryPagination.currentPage,
        limit: this.commentsHistoryPagination.pageSize,
        sort_by: 'approved_at',
        sort_order: 'desc'
      });
      const list = this.getCommentListFromResponse(response);
      this.commentsHistoryData = list.map((item: any) => ({
        id: item.id ?? item.comment_id ?? '',
        userName: item.user_name ?? item.userName ?? item.author_name ?? item.user?.name ?? 'Unknown',
        discussionName: item.discussion_name ?? item.discussion_title ?? item.title ?? '—',
        comment: this.stripHtml(item.comment ?? item.content ?? item.body ?? ''),
        submissionDate: this.formatService.formatDate(item.submission_date ?? item.created_at ?? '') ?? '',
        reviewDate: this.formatService.formatDate(item.review_date ?? item.reviewed_at ?? item.approved_at ?? '') ?? '',
        status: this.mapStatusToDisplay(item.status ?? item.review_status ?? '')
      }));
      const pag = response?.pagination ?? response?.data?.pagination;
      this.commentsHistoryPagination = {
        ...this.commentsHistoryPagination,
        totalItems: pag?.total ?? pag?.total_items ?? this.commentsHistoryData.length
      };
    } catch (error: any) {
      console.error('Error loading comments review history:', error);
      this.commentsHistoryData = [];
      this.commentsHistoryPagination.totalItems = 0;
    } finally {
      this.isLoadingCommentsHistory = false;
      setTimeout(() => this.reassignCommentsHistoryCellTemplates(), 0);
      this.cdr.detectChanges();
    }
  }

  /** Load Reported Comments pill - calls reportedComments(). */
  async loadCommentsReported(): Promise<void> {
    try {
      this.closeCommentsReportedDropdown();
      this.isLoadingCommentsReported = true;
      this.cdr.detectChanges();
      const response = await this.discussionApiService.reportedComments({
        page: this.commentsReportedPagination.currentPage,
        limit: this.commentsReportedPagination.pageSize,
        sort_by: 'CREATED_AT',
        sort_order: 'desc'
      });
      const list = this.getCommentListFromResponse(response);
      this.commentsReportedData = list.map((item: any) => ({
        id: item.id ?? item.report_id ?? '',
        reportedUserName: item.reported_user_name ?? item.reportedUserName ?? item.reporter_name ?? item.user_name ?? 'Unknown',
        discussionName: item.discussion_name ?? item.discussion_title ?? item.title ?? '—',
        reason: item.reason ?? item.report_reason ?? '',
        comment: this.stripHtml(item.comment ?? item.content ?? item.body ?? ''),
        reportedDate: this.formatService.formatDate(item.reported_date ?? item.created_at ?? item.reported_at ?? '') ?? ''
      }));
      const pag = response?.pagination ?? response?.data?.pagination;
      this.commentsReportedPagination = {
        ...this.commentsReportedPagination,
        totalItems: pag?.total ?? pag?.total_items ?? this.commentsReportedData.length
      };
    } catch (error: any) {
      console.error('Error loading reported comments:', error);
      this.commentsReportedData = [];
      this.commentsReportedPagination.totalItems = 0;
    } finally {
      this.isLoadingCommentsReported = false;
      setTimeout(() => this.reassignCommentsReportedCellTemplates(), 0);
      this.cdr.detectChanges();
    }
  }

  /** Load Report History pill - calls reportedHistory(). */
  async loadCommentsReportedHistory(): Promise<void> {
    try {
      this.isLoadingCommentsReportHistory = true;
      this.cdr.detectChanges();
      const response = await this.discussionApiService.reportedHistory({
        page: this.commentsReportHistoryPagination.currentPage,
        limit: this.commentsReportHistoryPagination.pageSize,
        sort_by: 'REVIEWED_AT',
        sort_order: 'desc'
      });
      const list = this.getCommentListFromResponse(response);
      this.commentsReportHistoryData = list.map((item: any) => ({
        id: item.id ?? item.report_id ?? '',
        reportedUserName: item.reported_user_name ?? item.reportedUserName ?? item.reporter_name ?? item.user_name ?? 'Unknown',
        discussionName: item.discussion_name ?? item.discussion_title ?? item.title ?? '—',
        reason: item.reason ?? item.report_reason ?? '',
        comment: this.stripHtml(item.comment ?? item.content ?? item.body ?? ''),
        reportedDate: this.formatService.formatDate(item.reported_date ?? item.created_at ?? item.reported_at ?? '') ?? '',
        reviewDate: this.formatService.formatDate(item.review_date ?? item.reviewed_at ?? '') ?? '',
        status: this.mapStatusToDisplay(item.status ?? item.review_status ?? '')
      }));
      const pag = response?.pagination ?? response?.data?.pagination;
      this.commentsReportHistoryPagination = {
        ...this.commentsReportHistoryPagination,
        totalItems: pag?.total ?? pag?.total_items ?? this.commentsReportHistoryData.length
      };
    } catch (error: any) {
      console.error('Error loading reported comment history:', error);
      this.commentsReportHistoryData = [];
      this.commentsReportHistoryPagination.totalItems = 0;
    } finally {
      this.isLoadingCommentsReportHistory = false;
      setTimeout(() => this.reassignCommentsReportHistoryCellTemplates(), 0);
      this.cdr.detectChanges();
    }
  }

  /** Reassign cell templates after comments data loads (fixes Action button after approve/reject). */
  private reassignCommentsPendingCellTemplates(): void {
    if (!this.commentsPendingActionCellTemplate) return;
    this.commentsPendingColumns = withCellTemplates(this.commentsPendingColumns, [
      { key: 'action', cellTemplate: this.commentsPendingActionCellTemplate }
    ]);
    this.cdr.detectChanges();
  }

  private reassignCommentsHistoryCellTemplates(): void {
    if (!this.commentsHistoryStatusCellTemplate) return;
    this.commentsHistoryColumns = withCellTemplates(this.commentsHistoryColumns, [
      { key: 'status', cellTemplate: this.commentsHistoryStatusCellTemplate }
    ]);
    this.cdr.detectChanges();
  }

  private reassignCommentsReportedCellTemplates(): void {
    if (!this.commentsReportedActionCellTemplate) return;
    this.commentsReportedColumns = withCellTemplates(this.commentsReportedColumns, [
      { key: 'action', cellTemplate: this.commentsReportedActionCellTemplate }
    ]);
    this.cdr.detectChanges();
  }

  private reassignCommentsReportHistoryCellTemplates(): void {
    if (!this.commentsReportHistoryStatusCellTemplate) return;
    this.commentsReportHistoryColumns = withCellTemplates(this.commentsReportHistoryColumns, [
      { key: 'status', cellTemplate: this.commentsReportHistoryStatusCellTemplate }
    ]);
    this.cdr.detectChanges();
  }
}
